import React, { useEffect, useRef, useState } from 'react'
import { Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { getConfigs } from '../../../config';
import { loadLeadsSDK } from '../../../helpers/leadsSDK';
import useTurnstile from '../../../hooks/useTurnstile';

/**
 * Identifies this form to leads-api, which resolves it to the Zapier hook server
 * side (svc-leads-api configs/zaps.js). An identifier, not a secret: the hook url
 * never ships to the browser.
 */
export const BUSINESS_QUOTE_FORM_ID = 'ff0eea26-c647-4dd9-96cf-1ef26d556673';

/**
 * Turnstile action; siteverify echoes it back and leads-api rejects a mismatch.
 */
export const BUSINESS_QUOTE_ACTION = 'business-quote';

const ContactForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // Synchronous guard: a second onFinish can start before `submitting` re-renders
  // the button disabled, and its getToken() would supersede the first one's.
  const inFlightRef = useRef(false);
  const { LEADS_SDK_SCRIPT_SRC: leadsSDKSrc, TURNSTILE_SITE_KEY: turnstileSiteKey } = getConfigs();
  const { containerRef, getToken } = useTurnstile({ siteKey: turnstileSiteKey, action: BUSINESS_QUOTE_ACTION });

  // Load the SDK when the form is displayed, not on every checkout page. A failure
  // here is retried on submit, which is where it is reported.
  useEffect(() => {
    loadLeadsSDK(leadsSDKSrc).catch(() => {});
  }, [leadsSDKSrc]);

  const onFinish = async (values) => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setSubmitting(true);

    try {
      const leads = await loadLeadsSDK(leadsSDKSrc);
      // Minted last: tokens are single use and expire after 300s.
      const token = await getToken();
      const { success } = await leads.submitForm({
        formId: BUSINESS_QUOTE_FORM_ID,
        token,
        payload: {
          fullName: values.name,
          email: values.email,
          phone: values.phone,
          comments: values.comments || '',
        },
      });

      if (success) {
        form.resetFields();
        alert(t('contactForm.successMessage'));
      } else {
        // Fields are kept so the visitor can resubmit; getToken() mints a new token.
        alert(t('contactForm.errorMessage'));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ContactForm submission failed:', error);
      alert(t('contactForm.networkError'));
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="business-contact-form">
      <h2 className="contact-heading">{t('contactForm.heading')}</h2>
      <Form form={form} onFinish={onFinish} className="form-info-tab">
        <Form.Item
          name="name"
          // whitespace: leads-api trims, so a blank-looking name is refused there.
          rules={[{ required: true, whitespace: true, message: t('contactForm.nameRequired') }]}
        >
          <Input placeholder={t('contactForm.fullNamePlaceholder')} className="input-box" maxLength="30" />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: t('contactForm.emailRequired') },
            { type: 'email', message: t('contactForm.emailInvalid') }
          ]}
        >
          <Input placeholder={t('contactForm.emailPlaceholder')} className="input-box" maxLength="30" />
        </Form.Item>

        <Form.Item
          name="phone"
          rules={[
            { required: true, message: t('contactForm.phoneRequired') },
            {
              // Mirrors leads-api, which refuses anything libphonenumber rejects for US.
              validator: (_, value) => (!value || isValidPhoneNumber(value, 'US')
                ? Promise.resolve()
                : Promise.reject(new Error(t('validation.invalidPhoneNumber')))),
            },
          ]}
        >
          <Input placeholder={t('contactForm.phonePlaceholder')} className="input-box" maxLength="15" />
        </Form.Item>

        <Form.Item name="comments">
          {/* leads-api caps comments at 2000; longer is refused, not truncated. */}
          <Input.TextArea placeholder={t('contactForm.commentsPlaceholder')} rows={4} className="input-box" maxLength={2000} />
        </Form.Item>

        <div ref={containerRef} className="contact-turnstile" />

        <Form.Item>
          <Button type="primary" htmlType="submit" className="submit-btn contact-submit-btn" loading={submitting} disabled={submitting}>
            {t('contactForm.submit')}
          </Button>
        </Form.Item>
        <p className="contact-disclaimer">{t('contactForm.disclaimer')}</p>
      </Form>
    </div>
  )
}

export default ContactForm
